import { RestApi } from '../restApi';

import type {
  GetUserBalanceResponse,
  GetUserConversationsRESTResponse,
  GetUserNotificationSettingsResponse,
  GetUserPaymentDataRESTResponse,
  GetRedeemAccountRestResponse,
  CreateRedeemAccountRequest,
  RedeemAccountResponse,
  RedeemRequestResponse,
  ValidateBsbResponse,
  GetUserPetRestResponse,
  GetUserPetsRestResponse,
  GetUserResponse,
  GetPersonalDetailsResponse,
  PersonalDetails,
  UpdatePersonalDetailsResponse,
  UpdateUserNotificationPrefsResponse,
  ValidateUserBalanceResponse,
} from './types';
import type { AxiosError, AxiosResponse } from 'axios';
import type { UserBalances, UserDetails } from '~/common/types/user';

export class UserApi extends RestApi {
  static async createRedeemAccount(
    data: CreateRedeemAccountRequest
  ): Promise<RedeemAccountResponse> {
    try {
      const response: AxiosResponse<RedeemAccountResponse> = await this.woofApi.post(
        'v1/redeem_accounts',
        data
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async createRedeemRequest(): Promise<RedeemRequestResponse> {
    try {
      const response: AxiosResponse<RedeemRequestResponse> = await this.woofApi.post(
        'v1/redeem_requests'
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async deleteUserPet(petId: number): Promise<string | null> {
    try {
      const response: AxiosResponse<{ message: string }> = await this.woofApi.delete(
        `v1/pets/${petId}`
      );
      return response.data.message;
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async getPersonalDetails(headers?: Record<string, string>): Promise<PersonalDetails> {
    try {
      const response: AxiosResponse<GetPersonalDetailsResponse> = await this.woofApi.get(
        'v1/users/details',
        { headers }
      );

      return { ...response.data.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async getUserBalance(
    userId: number,
    headers?: Record<string, string>
  ): Promise<UserBalances> {
    try {
      const response: AxiosResponse<GetUserBalanceResponse> = await this.woofApi.get(
        `v1/users/${userId}/balance`,
        { headers }
      );

      return { ...response.data.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async getUserConversations(
    limit = 20,
    archived = false,
    page = 1,
    headers?: Record<string, string>
  ): Promise<GetUserConversationsRESTResponse> {
    try {
      const response: AxiosResponse<GetUserConversationsRESTResponse> = await this.woofApi.get(
        `v3/conversations`,
        {
          headers,
          params: {
            limit,
            archived,
            page,
          },
        }
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async getUserDetails(headers?: Record<string, string>): Promise<UserDetails> {
    try {
      const { data } = await this.woofApi.get<GetUserResponse>('/v1/user', { headers });

      const { providesRwb, providesRwbPerService, roles, ...userDetails } = data.data;
      const isSitter = roles.data.some((role) => role.slug === 'sitter');

      return { isSitter, ...userDetails, roles };
    } catch (error) {
      this.handleError(error as AxiosError);

      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async getUserNotificationSettings(
    headers?: Record<string, string>
  ): Promise<GetUserNotificationSettingsResponse['data']> {
    try {
      const response: AxiosResponse<GetUserNotificationSettingsResponse> = await this.woofApi.get(
        'v1/users/notificationSettings',
        { headers }
      );

      return { ...response.data.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async getUserPaymentData(
    userId: number,
    headers?: Record<string, string>
  ): Promise<GetUserPaymentDataRESTResponse['data']> {
    try {
      const response: AxiosResponse<GetUserPaymentDataRESTResponse> = await this.woofApi.get(
        `v3/users/${userId}/user_payment_method`,
        { headers }
      );

      const responseData = response.data.data;

      if (Array.isArray(responseData.card)) {
        responseData.card = null;
      }
      if (Array.isArray(responseData.becs)) {
        responseData.becs = null;
      }

      return { ...response.data.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async getUserPet(
    petId: number,
    headers?: Record<string, string>
  ): Promise<GetUserPetRestResponse['data']> {
    try {
      const response: AxiosResponse<GetUserPetRestResponse> = await this.woofApi.get(
        `/v1/pets/profile/${petId}`,
        { headers }
      );

      return { ...response.data.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async getUserPets(
    userId: number,
    headers?: Record<string, string>
  ): Promise<GetUserPetsRestResponse['data']> {
    try {
      const response: AxiosResponse<GetUserPetsRestResponse> = await this.woofApi.get(
        `v1/users/${userId}/pets`,
        { headers }
      );

      return [...response.data.data];
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  // SSR callers pass auth per request to avoid mutating the shared Axios
  // client.
  static async getUserRedeemAccountDetails(
    headers?: Record<string, string>
  ): Promise<GetRedeemAccountRestResponse['data']> {
    try {
      const response: AxiosResponse<GetRedeemAccountRestResponse> = await this.woofApi.get(
        'v1/user/redeem_account',
        { headers }
      );

      return { ...response.data.data };
    } catch (error) {
      this.handleError(error as AxiosError);

      throw error;
    }
  }

  static async getUserUnreadMessagesCount(): Promise<number | null> {
    try {
      const response: AxiosResponse<{ data: { count: number } }> = await this.woofApi.get(
        'v1/messages/unread/count'
      );

      return response.data.data.count;
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  static async sendEmailVerification(email: string): Promise<string | null> {
    try {
      const response: AxiosResponse<{
        message: string;
      }> = await this.woofApi.post('v1/emails/send_verification', { email });

      return response.data.message;
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async sendMobileVerification(contactNumber: string): Promise<string | null> {
    try {
      const response: AxiosResponse<{
        message: string;
      }> = await this.woofApi.post('v1/phones/send_verification', {
        contactNumber: contactNumber.replace(/\s+/g, ''),
      });

      return response.data.message;
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async updatePersonalDetails(data: FormData): Promise<UpdatePersonalDetailsResponse> {
    try {
      const response: AxiosResponse<UpdatePersonalDetailsResponse> = await this.woofApi.post(
        'v1/users/details',
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async updateUserNotificationSettings({
    bookingStatusUpdates,
    newMessages,
  }: {
    bookingStatusUpdates: boolean;
    newMessages: boolean;
  }): Promise<UpdateUserNotificationPrefsResponse> {
    try {
      const response: AxiosResponse<UpdateUserNotificationPrefsResponse> = await this.woofApi.patch(
        'v1/users/notificationSettings',
        { bookingStatusUpdates, newMessages }
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async validateBsb(routingNumber: string): Promise<ValidateBsbResponse['data']> {
    try {
      const response: AxiosResponse<ValidateBsbResponse> = await this.woofApi.post(
        'v1/bsb_bank_numbers/validate',
        { routingNumber }
      );

      return { ...response.data.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async validateCreditsCode(code: string): Promise<ValidateUserBalanceResponse | null> {
    try {
      const response: AxiosResponse<ValidateUserBalanceResponse> = await this.woofApi.post(
        'v1/giftcards',
        { code }
      );

      return { ...response.data };
    } catch (error) {
      this.handleError(error as AxiosError);
      throw error;
    }
  }

  static async validateUserPromoCode({
    code,
    userId,
    bookingId,
  }: {
    bookingId: number;
    code: string;
    userId: number;
  }): Promise<boolean> {
    try {
      await this.woofApi.post(`v1/users/${userId}/promo`, {
        code,
        bookingId,
      });

      return true;
    } catch (error) {
      this.handleError(error as AxiosError, [422]);
      throw error;
    }
  }
}
